import { useApolloClient } from '@apollo/client';
import { ChatOrderGuidesInfo } from '@choco-admin/types';
import { useCallback } from 'react';

import {
  OrderGuideUploadPageGetChatOrderGuidesDocument,
  OrderGuideUploadPageGetChatOrderGuidesQuery,
  OrderGuideUploadPageGetChatOrderGuidesQueryVariables,
} from '../../../generated';
import { mapChatOrderGuides } from '../utils/mapping';

const LOOKUP_CONCURRENCY = 5;

/**
 * Imperative lookup of many chats (one CSV can target dozens). Returns a
 * map keyed by chat id; a null value means the chat does not exist.
 * Throws on network/auth errors so the caller can show a retryable error.
 */
export const useChatOrderGuides = () => {
  const client = useApolloClient();

  const fetchChatOrderGuides = useCallback(
    async (
      chatIds: string[]
    ): Promise<Map<string, ChatOrderGuidesInfo | null>> => {
      const results = new Map<string, ChatOrderGuidesInfo | null>();
      const queue = [...new Set(chatIds)];

      const worker = async (): Promise<void> => {
        const chatId = queue.shift();
        if (chatId === undefined) return;
        const { data } = await client.query<
          OrderGuideUploadPageGetChatOrderGuidesQuery,
          OrderGuideUploadPageGetChatOrderGuidesQueryVariables
        >({
          query: OrderGuideUploadPageGetChatOrderGuidesDocument,
          variables: { id: chatId },
          fetchPolicy: 'network-only',
          // GraphQL-level errors (e.g. malformed id) count as "not found";
          // network/auth failures still throw and surface as a retryable error.
          errorPolicy: 'all',
        });
        results.set(chatId, data?.chat ? mapChatOrderGuides(data.chat) : null);
        await worker();
      };

      await Promise.all(
        Array.from({ length: LOOKUP_CONCURRENCY }, () => worker())
      );
      return results;
    },
    [client]
  );

  return { fetchChatOrderGuides };
};
